<template>
  <div>
    <!-- sid 'en': logo with text instead of the photo hero with counters -->
    <section v-if="isHeroLogo" class="container py-4">
      <div class="row align-items-center g-4 bg-white p-3 p-md-5">
        <div class="col-12 col-lg-6 text-center text-lg-start">
          <img :src="require('@/assets/img/banner/en/welcome_logo.jpg')" alt="Jobcenter EN" class="img-fluid" />
        </div>
        <div class="col-12 col-lg-6">
          <p class="m-0">{{ heroText }}</p>
        </div>
      </div>
    </section>
    <ts-page-hero v-else :image="heroImage" alt="" :stats="heroStats" />

    <!-- sids 'aba'/'abatest' have no search on the welcome page -->
    <ts-jobs-search-card v-if="isHide" />

    <div v-if="contentTitle || contentDescription" class="container talentspace-welcome-heading">
      <div class="talentspace-welcome-heading-block d-flex">
        <div class="talentspace-welcome-heading-bar flex-shrink-0" aria-hidden="true"></div>
        <div>
          <!-- Title and subline come from PAT and may contain markup -->
          <h1 class="fw-bold mb-0" v-html="contentTitle"></h1>
          <p class="mb-0" v-html="contentDescription"></p>
        </div>
      </div>
    </div>

    <div class="container pb-5">
      <div class="d-flex flex-column gap-3">
        <section v-if="intro.introTitleId" class="ts-welcome-info">
          <article class="ts-welcome-info-card d-flex w-100 flex-column">
            <h2 class="fw-bold mb-3">{{ translations[intro.introTitleId] }}</h2>
            <p class="ts-welcome-intro mb-4">{{ translations[intro.introTextId] }}</p>
            <div class="row g-4">
              <div v-for="feature in features" :key="feature.link" class="col-12 col-md-4">
                <article class="ts-welcome-feature position-relative h-100">
                  <div class="ts-welcome-feature-icon d-inline-flex align-items-center justify-content-center mb-3">
                    <i class="bi" :class="feature.icon" aria-hidden="true"></i>
                  </div>
                  <h3 class="d-flex align-items-center gap-2 fw-bold mb-2">
                    <router-link class="stretched-link ts-welcome-feature-link" :to="{ path: feature.link, query: { lang } }">
                      {{ feature.title }}
                    </router-link>
                    <i class="bi bi-arrow-right ts-welcome-feature-arrow" aria-hidden="true"></i>
                  </h3>
                  <p class="mb-0">{{ feature.text }}</p>
                </article>
              </div>
            </div>
          </article>
        </section>

        <ts-audience-cards />

        <ts-field-grid
          v-if="clusters.length"
          :title="fieldsTitle"
          :fields="clusters"
          :unit="translations[13439]"
          @select="searchCluster"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { AppModule, SettingsCounters } from '@/store/modules/app';
import AnalysisMixin from '@/mixins/AnalysisMixin';
import { AnalysisType } from '@/types/Analysis';
import { ThemesModule } from '@/store/modules/themes';
import { portalBannerGroup } from '@/config/configuration';
import { pageHeroImage } from '@/helpers/heroImage';
import { SERVER_URL } from '@/config/constant';
import { applyClusterJobSearch, JobCluster, jobClusters } from '@/sections/jobs/search';
import { siteContent } from '@/sites';
import TsPageHero, { HeroStat } from '@/components/Talentspace/layout/TsPageHero.vue';
import TsJobsSearchCard from '@/components/Talentspace/search/TsJobsSearchCard.vue';
import TsAudienceCards from '@/components/Talentspace/blocks/TsAudienceCards.vue';
import TsFieldGrid from '@/components/Talentspace/blocks/TsFieldGrid.vue';

@Component({
  metaInfo(this: any): any {
    return {
      title: this.pageTitles[this.lang] || this.pageTitles.de,
      meta: [{ name: 'description', content: this.pageDescriptions[this.lang] || this.pageDescriptions.de }],
    };
  },
  components: {
    TsPageHero,
    TsJobsSearchCard,
    TsAudienceCards,
    TsFieldGrid,
  },
})
export default class WelcomeView extends mixins(AnalysisMixin) {
  created() {
    this.setAnalysis(AnalysisType.PORTAL_START);
  }

  // ─── meta ────────────────────────────────────────────────────────────────────

  pageDescriptions = {
    de: 'Jobs in Österreich suchen & finden ✔ Jobbörse für Österreich ✔ Jobaustausch für Arbeitnehmer & Arbeitgeber ✔ Jetzt informieren & in Österreich arbeiten!',
    en: 'Austria job search ✔ job offers for employers ✔ vacancies in Austria ✔ Search for industries and locations ✔ Find out now and work in Austria!',
  };

  get pageTitles() {
    return {
      de: this.title || this.titleSid.de,
      en: this.title || this.titleSid.en,
    };
  }

  get titleSid(): { de: string, en: string } {
    if (ThemesModule.sample.title) {
      return ThemesModule.sample.title;
    }
    return {
      de: 'Arbeiten in Österreich – Jobs suchen ✔ | WORK IN AUSTRIA',
      en: 'Working in Austria – find jobs in austria | WORK IN AUSTRIA',
    };
  }

  get title() {
    return AppModule.settingPortal.html_title;
  }

  // ─── portal ──────────────────────────────────────────────────────────────────

  get translations() {
    return AppModule.translation;
  }

  get lang() {
    return AppModule.language;
  }

  get sid() {
    return AppModule.sid;
  }

  get isHide() {
    return !['aba', 'abatest'].includes(this.sid);
  }

  get contentTitle() {
    return AppModule.settingPortal.content_title;
  }

  get contentDescription() {
    return AppModule.settingPortal.content_description;
  }

  // ─── hero ────────────────────────────────────────────────────────────────────

  // sid 'en': logo with text instead of the photo hero
  get isHeroLogo() {
    return this.sid === 'en';
  }

  heroText = 'Finden Sie passende Stellenangebote im Ennepe-Ruhr-Kreis und in der Umgebung. '
    + 'Das Jobcenter EN Stellenportal bringt Arbeitssuchende und regionale Arbeitgeber einfach und direkt zusammen.';

  // Banner from PAT (portalBanner sids) → the sid's own banner or the Talentspace photo
  get heroImage(): string {
    const headerImage = AppModule.settingPortal.header_image;
    if (portalBannerGroup(this.sid) && headerImage) {
      return `${SERVER_URL}${headerImage}`;
    }
    return pageHeroImage(this.sid, 'welcome');
  }

  // Counters shown and their order come from PAT (settingPortal.counters)
  get heroStats(): HeroStat[] {
    const { setting } = AppModule;
    return [
      {
        name: 'companies' as const, count: Number(setting?.companies_count || 0), label: this.translations[14670], link: '/company',
      },
      {
        name: 'applicants' as const,
        count: Number(setting?.applicant_search_tab?.overall_applicants_count || 0),
        label: this.translations[14671],
        link: '/workers',
      },
      {
        name: 'jobs' as const, count: Number(setting?.all_count || 0), label: this.translations[14672], link: '/jobs',
      },
      {
        name: 'courses' as const,
        count: Number(setting?.courses_search_tab?.overall_courses_count || 0),
        label: this.translations[15404],
        link: '/courses',
      },
    ]
      .filter((stat) => this.counter(stat.name)?.visible)
      .sort((a, b) => this.counterOrder(a.name) - this.counterOrder(b.name))
      .map(({ count, label, link }) => ({ count, label, link: { path: link, query: { lang: this.lang } } }));
  }

  counter(name: SettingsCounters['name']) {
    return AppModule.settingPortal.counters.find((el) => el.name === name);
  }

  counterOrder(name: SettingsCounters['name']) {
    return AppModule.settingPortal.counters.findIndex((el) => el.name === name);
  }

  // ─── content blocks ──────────────────────────────────────────────────────────

  // "What is the Talent Space" block: brand text of the site, hidden when the site has none
  get intro() {
    return siteContent(this.sid);
  }

  get features() {
    return [
      {
        icon: 'bi-briefcase', title: AppModule.translation[16581], text: AppModule.translation[16582], link: '/jobs',
      },
      {
        icon: 'bi-people', title: AppModule.translation[16583], text: AppModule.translation[16584], link: '/workers',
      },
      {
        icon: 'bi-book', title: AppModule.translation[16585], text: AppModule.translation[16586], link: '/courses',
      },
    ];
  }

  get fieldsTitle() {
    return AppModule.translation[16578];
  }

  get clusters() {
    return jobClusters();
  }

  searchCluster(cluster: JobCluster) {
    applyClusterJobSearch({ id: cluster.id, title: cluster.name }, true);
    this.$router.push({ name: 'Jobs', query: { lang: this.lang } });
  }
}
</script>
